<template>
  <v-form @submit.prevent="submitForm" ref="formRef" v-model="formValid">
    <v-card class="px-5 py-3">
      <v-card-title class="d-flex justify-space-between align-center mb-3">
        <span>Editar</span>
      </v-card-title>

      <v-card-text>
        <v-row v-if="loading" class="ma-12">
          <v-spacer/>
          <v-progress-circular indeterminate></v-progress-circular>
          <v-spacer/>
        </v-row>
        <v-row v-else>
          <template v-for="field in visibleFields" :key="field.value">
            <v-col :cols="field.col || 12">
              <component
                v-if="!field.isJsonArray"
                :is="field.component"
                v-model="form[field.value]"
                v-bind="getFieldProps(field)"
                :rules="field.required ? [v => !!v || 'Campo obligatorio'] : []"
              />
              <component
                v-else
                :is="field.component"
                v-model="form[field.value]"
                :fields="field.children"
                :summary="field.summary"
                :label="field.label"
              />
            </v-col>
          </template>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="closeForm">Cancelar</v-btn>
        <v-btn type="submit" :disabled="!formValid" :loading="is_saving" color="secondary">Guardar</v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script setup lang="ts">
import { ref, onMounted, watchEffect, toRaw } from 'vue';
import { useRoute } from 'vue-router';
import { configRegistry } from '@/lib/configRegistry';
import { remoteOptionsRegistry } from '@/lib/remoteOptions';
import { useFloatFormStore } from '@/store/floatForm';
import type { FormField, FormProps } from '@/types/form';

const loading = ref(true);
const is_saving = ref(false);

const route = useRoute();
const moduleName = (route.meta.moduleName ?? route.name) as string;
const floatFormStore = useFloatFormStore();
const props = floatFormStore.props as FormProps;
const onUpdate = props?.onUpdate;
const item = props?.item || {};

const formFields = ref<FormField[]>([]);
const form = ref<Record<string, any>>({});
const formValid = ref(true);
const formRef = ref();
const visibleFields = ref<FormField[]>([]);

watchEffect(() => {
  visibleFields.value = formFields.value
    .map((field) => {
      const val = form.value[field.dependsOn as any];
      const condition = field.dependsCondition;
      let conditionMet = true;

      if (field.dependsOn && field.dependsBehavior) {
        switch (condition?.condition) {
          case 'diferent':
            conditionMet = val !== undefined && val !== '' && val !== condition.value;
            break;
          case 'equals':
            conditionMet = val === condition.value;
            break;
          case 'includes':
            conditionMet = !JSON.stringify(val)?.toLowerCase?.().includes(condition.value);
          break;
        }

        if (field.dependsBehavior === 'hidden' && !conditionMet) return null;
        if (field.dependsBehavior === 'disabled' && !conditionMet) {
          return { ...field, permission: 'disabled' };
        }
      }

      if (field.permission === 'hidden') return null;

      return field;
    })
    .filter((f): f is FormField => !!f);
});

const getFieldProps = (field: FormField) => {
  const { children, component, value, transform, parse, ...rest } = field;
  const props: Record<string, any> = {
    ...rest,
    label: field.label,
    clearable: true,
    density: 'compact',
    rules: field.required ? [(v: any) => !!v || 'Campo obligatorio'] : [],
    'item-title': field.itemText || 'label',
    'item-value': field.itemValue || 'value',
    disabled: field.permission === 'disabled',
    readonly: field.readonly,
    'prepend-inner-icon': field.icon,
  };

  if (field.options) props.items = field.options;

  if (field.fetchOptions && remoteOptionsRegistry[field.fetchOptions]) {
    props['onUpdate:search'] = async (val: string) => {
      if (!val || val.length < 2) return;
      const fetchFn = remoteOptionsRegistry[field.fetchOptions!];
      const results = await fetchFn(val);
      field.options = results;
      props.items = results;
    };
  }

  return props;
};

const flattenForm = (form: Record<string, any>, fields: FormField[]) => {
  const flatForm: Record<string, any> = { id: form.id };

  for (const field of fields) {
    const val = form[field.value];

    if (typeof field.transform === 'function') {
      const result = field.transform(toRaw(val), form);
      if (typeof result === 'object' && !Array.isArray(result)) {
        Object.assign(flatForm, result);
      } else {
        flatForm[field.value] = result;
      }
      continue;
    }

    if (field.isJsonArray && field.children) {
      if(field.plainJson){
        for (const child of field.children) {
          flatForm[child.value] = val?.[child.value] ?? null;
        }
      }else{
        flatForm[field.value] = toRaw(val);
      }
    } else if (field.multiple && Array.isArray(val)) {
      flatForm[field.value] = val.map((v: any) =>
        typeof v === 'object' ? v[field.itemValue || 'value'] ?? v.id ?? null : v
      );
    } else if (typeof val === 'object' && val !== null) {
      flatForm[field.value] = val[field.itemValue || 'value'] ?? val.id ?? null;
    } else {
      flatForm[field.value] = val ?? null;
    }
  }

  return flatForm;
};

const resetForm = () => {
  formFields.value.forEach(f => (form.value[f.value] = null));
};

const closeForm = () => {
  resetForm();
  floatFormStore.closeForm();
};

const submitForm = async () => {
  if (!formRef.value?.validate()) return;
  is_saving.value = true;
  try {
    const payload = flattenForm(form.value, formFields.value);
    payload.id = item.id;

    if (onUpdate) {
      await onUpdate(payload);
    } else {
      console.warn('[EditForm] No se proporcionó función onUpdate');
    }

    closeForm();
    is_saving.value = false;
  } catch (err) {
    is_saving.value = false;
    console.error('[EditForm] Error en onUpdate', err);
  }
};

onMounted(async () => {
  const loader = configRegistry[moduleName];
  if (!loader) {
    console.error(`[EditForm] Config para ${moduleName} no encontrada.`);
    return;
  }

  try {
    const mod = await loader();
    formFields.value = mod.EDIT_FORM || [];

    for (const f of formFields.value) {
      let raw = item[f.value] ?? item.attributes?.[f.value];
      if (f.isJsonArray && f.plainJson && f.children) {
        const group: Record<string, any> = {};
        for (const child of f.children) {
          group[child.value] = item[child.value] ?? item.attributes?.[child.value] ?? null;
        }
        raw = group;
      }
      // ✅ Aplica parse si está definido en el config
      if (typeof f.parse === 'function') {
        form.value[f.value] = f.parse(raw, item);

        continue;
      }
      // ✅ Caso general
      form.value[f.value] = raw ?? null;

      // ✅ Si tiene fetchOptions, buscar y asignar opción completa
      /*if (f.fetchOptions && remoteOptionsRegistry[f.fetchOptions]) {
        const result = await remoteOptionsRegistry[f.fetchOptions]();
        f.options = result;
        const itemValueKey = f.itemValue || 'value';
        if (Array.isArray(form.value[f.value])) {
          form.value[f.value] = form.value[f.value].map((v: any) =>
            typeof v === 'object' ? v : result.find(opt => opt[itemValueKey] === v) || v
          );
        } else {
          const val = form.value[f.value];
          if (val && typeof val !== 'object') {
            const match = result.find(opt => opt[itemValueKey] === val);
            if (match) form.value[f.value] = match;
          }
        }
      }*/
    }
    loading.value = false;
  } catch (err) {
    console.error('[EditForm] Error al cargar config:', err);
  }
});
</script>
